import logoSCA from "@assets/events-awards/logo-sca.png";
import { BlockTitle } from "@blocks/Blocks";

/**
 * Component to render SCA about page
 */
const SCAAboutPage = (): React.ReactElement => {
  return (
    <div data-testid="sca-about-page-component" className="font-public-sans">
      <BlockTitle
        text="History"
        rootClassName="pt-3 mb-6  text-4xl md:text-6xl lg:text-8xl font-medium"
      />

      <p>
        The Best Annual Report Award was first introduced in 1974 to encourage
        listed companies to increase the extent and quality of voluntary
        disclosures in their annual reports. Prior to the SCA, this competition
        was jointly organised over the years by the Institute of Singapore
        Chartered Accountants, Singapore Exchange Limited, Singapore Institute
        of Directors, The Business Times, Securities Investors Association
        (Singapore), and Investment Management Association of Singapore.
      </p>

      <p>
        The Best Managed Board Award, the first of its kind in the Asia Pacific
        region, was launched in 2003 to recognise and encourage enhanced
        corporate governance within companies.
      </p>

      <p>
        The Best Investor Relations Award and Best CFO Award were added in 2005
        to form the Singapore Corporate Awards. The Best CEO Award was added in
        2006.
      </p>

      <p>
        A special one-off Distinguished Contribution to Corporate Governance
        Award was given to Mr JY Pillay in 2015 for the tenth anniversary of the
        Singapore Corporate Awards.
      </p>

      <p>
        A Special Recognition Award was launched in 2016 to recognise
        outstanding and exemplary conduct related to corporate governance not
        covered by the other awards.
      </p>

      <p>
        The inaugural Best Risk Management Award is introduced in 2019 to
        recognise and raise awareness of the core and leading risk management
        practices required to support and sustain the strategic direction of
        companies, especially in this age of technology and disruption.
      </p>

      <div className="mb-12">
        <img
          src={logoSCA}
          alt="Singapore Corporate Awards logo"
          width={296}
          height={400}
        />
      </div>

      <BlockTitle
        text="Objectives"
        rootClassName="mb-6 text-4xl md:text-6xl lg:text-8xl font-medium"
      />

      <p>The objectives of the Singapore Corporate Awards are to:</p>

      <ul className="list-outside list-disc pl-8">
        <li>
          Recognise and honour SGX-listed companies as well as individuals who,
          through their corporate practices, have helped to raise
          Singapore&apos;s corporate disclosure standards and corporate
          governance.
        </li>
        <li>
          Showcase SGX-listed companies and key decision makers who effectively
          implement and promote corporate disclosure standards and corporate
          governance in their companies.
        </li>
        <li>
          Create a prestigious corporate event for listed companies&apos; Chief
          Executive Officers, Chief Financial Officers, Directors and
          substantial shareholders to network, socialise and share ideas and
          views on good corporate governance.
        </li>
      </ul>

      <BlockTitle
        text="Awards"
        rootClassName="mt-12 mb-6 text-4xl md:text-6xl lg:text-8xl font-medium"
      />

      <p>The Singapore Corporate Awards consists of six main awards:</p>

      <ul className="mb-6 list-outside list-disc pl-8">
        <li>Best Managed Board</li>
        <li>Best Chief Executive Officer (CEO)</li>
        <li>Best Chief Financial Officer (CFO)</li>
        <li>Best Risk Management</li>
        <li>Best Investor Relations</li>
        <li>Best Annual Report</li>
      </ul>

      <p>
        A special one-off Distinguished Contribution to Corporate Governance
        Award was given to Mr JY Pillay in 2015 for the tenth anniversary of the
        Singapore Corporate Awards.
      </p>

      <p>
        A Special Recognition Award was launched in 2016 to recognise recent
        actions and practices of boards and companies that demonstrate
        outstanding and exemplary corporate governance.
      </p>

      <p className="mb-0">
        In each of the main awards, there are three primary categories:
      </p>

      <ul className="mb-6 list-outside list-disc pl-8">
        <li>
          Big Caps (SGX-listed companies with market capitalisation above
          S$1billion)
        </li>
        <li>
          Mid Caps (SGX-listed companies with market capitalisation between
          S$300 million and S$1 billion)
        </li>
        <li>
          Small Caps (SGX-listed companies with market capitalisation below
          S$300 million)
        </li>
      </ul>

      <p>
        The Best Annual Report Award and the Best Investor Relations Award have
        two additional categories for First-Year Listed Companies, and REITs &
        Business Trusts.
      </p>

      <p>
        For the individual awards (Best CFO and Best CEO), there is one winner
        in each category.
      </p>

      <p>
        For the corporate awards (Best Annual Report, Best Investor Relations,
        Best Risk Management and Best Managed Board), there are three winners
        for Gold, Silver and Bronze in each category.
      </p>

      <p>
        Gold winners for the corporate awards, and Best CFO and Best CEO winners
        observe a two-year moratorium following their awards.
      </p>

      <p>In summary, the main awards and their categories are:</p>

      <table className="min-w-full table-auto border-collapse">
        <thead>
          <tr className="border-b border-b-2 border-b-black">
            <th className="p-2 text-left font-bold">Categories</th>
            <th className="p-2 text-left font-bold">Best Managed Board</th>
            <th className="p-2 text-left font-bold">Best CEO</th>
            <th className="p-2 text-left font-bold">Best CFO</th>
            <th className="p-2 text-left font-bold">Best Investor Relations</th>
            <th className="p-2 text-left font-bold">Best Annual Report</th>
          </tr>
        </thead>

        <tbody>
          <tr className="border-b">
            <td className="p-2 align-top align-top">Big Caps</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2 align-top align-top">One Winner</td>
            <td className="p-2 align-top align-top">One Winner</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
          </tr>

          <tr className="border-b">
            <td className="p-2 align-top">Mid Caps</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2 align-top">One Winner</td>
            <td className="p-2 align-top">One Winner</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
          </tr>

          <tr className="border-b">
            <td className="p-2 align-top">Small Caps</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2 align-top">One Winner</td>
            <td className="p-2 align-top">One Winner</td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
          </tr>

          <tr className="border-b">
            <td className="p-2 align-top">REITs & Business Trusts</td>
            <td className="p-2"></td>
            <td className="p-2"></td>
            <td className="p-2"></td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
            <td className="p-2">
              Gold
              <br />
              Silver
              <br />
              Bronze
            </td>
          </tr>

          <tr className="border-b">
            <td className="p-2 align-top">First-Year Listed Company</td>
            <td className="p-2"></td>
            <td className="p-2"></td>
            <td className="p-2"></td>
            <td className="p-2">Merit</td>
            <td className="p-2">Merit</td>
          </tr>
        </tbody>
      </table>

      <BlockTitle
        text="Organisations"
        rootClassName="pt-12 pb-6 text-4xl md:text-6xl lg:text-8xl font-medium"
      />

      <p>
        The Awards are organised by the Institute of Singapore Chartered
        Accountants (ISCA), Singapore Institute of Directors (SID) and The
        Business Times (BT), supported by Accounting and Corporate Regulatory
        Authority (ACRA) and Singapore Exchange (SGX).
      </p>

      <p>The current award partners are:</p>

      <ul className="mb-6 list-outside list-disc pl-8">
        <li>CGS, NUS Business School,</li>
        <li>Deloitte,</li>
        <li>EY,</li>
        <li>Heidrick & Struggles Inc,</li>
        <li>KPMG, and</li>
        <li>PwC.</li>
      </ul>

      <p>
        In addition, the supporting partner for media communication is Citigate
        Dewe Rogerson Singapore Pte Ltd.
      </p>
    </div>
  );
};

export default SCAAboutPage;
